// GA4 Ecommerce panel — revenue KPI row (Revenue / Transactions / AOV) above
// a Recharts revenue trend. Ports the ResponsiveContainer/CartesianGrid/
// Tooltip conventions from components/dashboard/TrendChart.tsx directly into
// this file so it can be dropped anywhere without wiring dependencies — the
// one exception is primitives.formatMoney (single source of truth for money
// formatting, 2026-08-10): this file's own local `formatMoney`/
// `CURRENCY_SYMBOLS` duplicate is deleted, not just unused. Its "plain $ for
// every dollar currency" capability now lives in primitives.formatMoney's
// `currencyDisplay` option instead of a second hand-rolled symbol map.
//
// Fixed hex (not theme CSS vars): the trend's azure line + accent-blue fill
// are a literal, theme-independent color spec (unlike `--color-coral`, which
// intentionally swaps value between light/dark) — same reasoning already
// used in frames/CallOutcomesDonut.tsx for outcome-color identity.

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatLocalDate, formatMoney } from "@/components/client-analytics/primitives";

export interface EcommerceRevenuePoint {
  date: string; // ISO date or YYYY-MM period label
  value: number;
}

export interface EcommerceRevenuePanelProps {
  revenue: number;
  transactions: number;
  aov?: number;
  currency?: string;
  over_time: EcommerceRevenuePoint[];
}

// Sample data for standalone preview / storybook-style rendering.
export const demoEcommerce: EcommerceRevenuePanelProps = {
  revenue: 48200,
  transactions: 213,
  aov: 226,
  currency: "USD",
  over_time: [
    { date: "2026-05", value: 9800 },
    { date: "2026-06", value: 12400 },
    { date: "2026-07", value: 11200 },
    { date: "2026-08", value: 14800 },
  ],
};

const CHART_HEIGHT = 180;
const AREA_STROKE = "#0A76A8"; // azure
const AREA_FILL = "rgba(76, 201, 240, 0.18)"; // accent blue, low-opacity fill

function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

// "2026-05" / "2026-05-01" -> "May". Falls back to the raw label when it
// doesn't parse as a date, so arbitrary period labels still render.
// Parsing goes through the shared primitives.parseLocalDate — see its comment
// for why `new Date("YYYY-MM-DD")` rendered a day early west of UTC. A monthly
// bucket ("2026-05") is widened to its first day before parsing; on Jan 1 the
// old UTC-midnight parse could roll the label back into the previous December.
function monthLabel(date: string): string {
  return formatLocalDate(date.length <= 7 ? `${date}-01` : date, { month: "short" }) ?? date;
}

function KpiCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 px-5 py-4 flex flex-col gap-1">
      <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-dash-value font-bold tabular text-foreground">{value}</div>
    </div>
  );
}

export function EcommerceRevenuePanel({
  revenue,
  transactions,
  aov,
  currency = "USD",
  over_time,
}: EcommerceRevenuePanelProps) {
  const resolvedAov =
    typeof aov === "number" ? aov : transactions > 0 ? revenue / transactions : undefined;
  const hasTrend = over_time && over_time.length > 0;
  const chartData = hasTrend
    ? over_time.map((p) => ({ ...p, label: monthLabel(p.date) }))
    : [];

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className={`flex flex-wrap divide-x divide-border${hasTrend ? " border-b border-border" : ""}`}>
        <KpiCell label="Revenue" value={formatMoney(revenue, currency)} />
        <KpiCell label="Transactions" value={formatCount(transactions)} />
        <KpiCell
          label="AOV"
          value={resolvedAov !== undefined ? formatMoney(resolvedAov, currency) : "—"}
        />
      </div>
      {/* Empty over_time renders KPIs only — never an empty axis box. */}
      {hasTrend && (
        <div className="p-3">
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <AreaChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }}
                tickFormatter={(v) => formatMoney(v, currency)}
                width={56}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid var(--color-border)",
                  fontSize: 12,
                }}
                formatter={(value: number) => [formatMoney(value, currency), "Revenue"]}
                labelFormatter={(label) => String(label)}
              />
              <Area
                type="monotone"
                dataKey="value"
                name="Revenue"
                stroke={AREA_STROKE}
                fill={AREA_FILL}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default EcommerceRevenuePanel;
